// @ts-ignore: moment declaration file
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.js'

import type { SearchSubmissionType } from '../types'
import { CheckoutApiError } from './api/use-reservation-checkout'

export const GENERIC_CHECKOUT_ERROR_MESSAGE = 'Došlo k neočekávané chybě.'

type RefetchActiveReservationResult = {
    status: string,
    data?: {
        garage?: {
            id: number | string,
        } | null,
        reservation: {
            state: string,
            begins_at: string,
            ends_at: string,
            requested_begins_at?: string | null,
            requested_ends_at?: string | null,
        },
    },
}

type PrepareCheckoutReservationParams = {
    garageId: number | string | undefined,
    searchSubmission: SearchSubmissionType,
    hasStoredReservationToken: boolean,
    refetchActiveReservation: () => Promise<RefetchActiveReservationResult>,
    createReservation: (payload: {
        begins_at: string,
        ends_at: string,
        garage_id: string,
    }) => Promise<unknown>,
    changeReservationGarage: (payload: {
        garage_id: string,
        begins_at: string,
        ends_at: string,
    }) => Promise<unknown>,
    changeReservationTime: (payload: {
        begins_at: string,
        ends_at: string,
    }) => Promise<unknown>,
    onSuccess: () => void | Promise<void>,
    onError: (message: string) => void,
}

function formatCheckoutDateTime(date: moment.Moment | null, time: string | null): string | null {
    if (!date || !time) {
        return null
    }

    return `${moment(date).format('YYYY-MM-DD')} ${time}`
}

function normalizeReservationDateTime(value: string): string {
    return moment.tz(value, 'Europe/Prague').format('YYYY-MM-DD HH:mm')
}

export async function prepareCheckoutReservation({
    garageId,
    searchSubmission,
    hasStoredReservationToken,
    refetchActiveReservation,
    createReservation,
    changeReservationGarage,
    changeReservationTime,
    onSuccess,
    onError,
}: PrepareCheckoutReservationParams): Promise<void> {
    const beginsAt = formatCheckoutDateTime(searchSubmission.startDate, searchSubmission.startTime)
    const endsAt = formatCheckoutDateTime(searchSubmission.endDate, searchSubmission.endTime)
    const garageIdValue = garageId?.toString()

    if (!garageIdValue || !beginsAt || !endsAt) {
        onError(GENERIC_CHECKOUT_ERROR_MESSAGE)
        return
    }

    let shouldCreateReservation = true

    if (hasStoredReservationToken) {
        let activeReservationResult: RefetchActiveReservationResult | null = null

        try {
            activeReservationResult = await refetchActiveReservation()
        } catch (error) {
            if (!(error instanceof CheckoutApiError) || (error.status !== 404 && error.status !== 410)) {
                throw error
            }
        }

        if (
            activeReservationResult?.status === 'success' &&
            activeReservationResult.data?.reservation.state === 'allocated'
        ) {
            shouldCreateReservation = false

            const { reservation } = activeReservationResult.data
            // Search controls use 00:01 as a placeholder for midnight.
            const comparisonBeginsAt = beginsAt.replace(/ 00:01$/, ' 00:00')
            const comparisonEndsAt = endsAt.replace(/ 00:01$/, ' 00:00')
            const garageChanged =
                activeReservationResult.data.garage?.id.toString() !== garageIdValue
            const beginsAtChanged =
                normalizeReservationDateTime(reservation.requested_begins_at ?? reservation.begins_at) !== comparisonBeginsAt
            const endsAtChanged =
                normalizeReservationDateTime(reservation.requested_ends_at ?? reservation.ends_at) !== comparisonEndsAt
            const timeChanged = beginsAtChanged || endsAtChanged

            if (!garageChanged && !timeChanged) {
                await onSuccess()
                return
            }

            if (garageChanged) {
                await changeReservationGarage({
                    garage_id: garageIdValue,
                    begins_at: beginsAt,
                    ends_at: endsAt,
                })
            } else if (timeChanged) {
                await changeReservationTime({
                    begins_at: beginsAt,
                    ends_at: endsAt,
                })
            }

            await onSuccess()
            return
        }
    }

    if (shouldCreateReservation) {
        await createReservation({
            begins_at: beginsAt,
            ends_at: endsAt,
            garage_id: garageIdValue,
        })

        await onSuccess()
    }
}
